import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Truck } from '@phosphor-icons/react';

export function DispatchEntryDialog({
  open, onOpenChange, selectedJo, dispatchForm, setDispatchForm,
  driverMaster, vehicleMaster, onSubmit, actionLoading
}) {
  const totalWindows = selectedJo?.no_of_windows || 0;
  const alreadyDispatched = selectedJo?.windows_dispatched || 0;
  const remaining = Math.max(0, totalWindows - alreadyDispatched);
  const isPartial = dispatchForm.dispatch_type === 'partial';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck size={22} weight="bold" className="text-orange-500" />
            Create Dispatch Entry - {selectedJo?.jo_number}
          </DialogTitle>
          <DialogDescription className="sr-only">Create a dispatch entry for the selected job order</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="bg-zinc-50 p-3 rounded-sm text-sm">
            <p><strong>{selectedJo?.customer_name}</strong> | {selectedJo?.work_location}</p>
            <div className="flex gap-4 mt-1 text-xs text-zinc-600">
              <span>Total: {totalWindows} windows</span>
              <span>Dispatched: {alreadyDispatched}</span>
              <span className="font-bold text-orange-600">Pending: {remaining}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Dispatch Date *</Label>
              <Input
                data-testid="dispatch-date-input"
                type="date"
                value={dispatchForm.dispatch_date}
                onChange={e => setDispatchForm(f => ({ ...f, dispatch_date: e.target.value }))}
                className="h-10"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Dispatch Type *</Label>
              <Select value={dispatchForm.dispatch_type} onValueChange={v => setDispatchForm(f => ({ ...f, dispatch_type: v, windows_dispatched: v === 'full' ? remaining : '' }))}>
                <SelectTrigger data-testid="dispatch-type-select" className="h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="partial">Partial Dispatch</SelectItem>
                  <SelectItem value="full">Full Dispatch</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {isPartial && (
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                No. of Windows to Dispatch * <span className="text-zinc-400">(max {remaining})</span>
              </Label>
              <Input
                data-testid="windows-dispatched-input"
                type="number"
                min={1}
                max={remaining}
                value={dispatchForm.windows_dispatched || ''}
                onChange={e => setDispatchForm(f => ({ ...f, windows_dispatched: parseInt(e.target.value) || '' }))}
                placeholder={`Enter number (1 - ${remaining})`}
                className="h-10"
              />
              {dispatchForm.windows_dispatched > remaining && (
                <p className="text-xs text-red-600 font-bold">Cannot exceed {remaining} pending windows</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Driver Name *</Label>
              {driverMaster.length > 0 ? (
                <Select
                  value={dispatchForm.driver_name}
                  onValueChange={v => {
                    const driver = driverMaster.find(d => d.name === v);
                    setDispatchForm(f => ({ ...f, driver_name: v, driver_contact: driver?.mobile || '' }));
                  }}
                >
                  <SelectTrigger data-testid="driver-name-select" className="h-10">
                    <SelectValue placeholder="Select driver" />
                  </SelectTrigger>
                  <SelectContent>
                    {driverMaster.map(d => (
                      <SelectItem key={d.name} value={d.name}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  data-testid="driver-name-input"
                  value={dispatchForm.driver_name}
                  onChange={e => setDispatchForm(f => ({ ...f, driver_name: e.target.value }))}
                  placeholder="Driver name"
                  className="h-10"
                />
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Driver Contact</Label>
              <Input
                data-testid="driver-contact-input"
                value={dispatchForm.driver_contact}
                onChange={e => setDispatchForm(f => ({ ...f, driver_contact: e.target.value }))}
                placeholder="Auto-filled from master"
                className="h-10"
                readOnly={driverMaster.length > 0 && dispatchForm.driver_name !== ''}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Vehicle Number *</Label>
            {vehicleMaster.length > 0 ? (
              <Select value={dispatchForm.vehicle_number} onValueChange={v => setDispatchForm(f => ({ ...f, vehicle_number: v }))}>
                <SelectTrigger data-testid="vehicle-number-select" className="h-10">
                  <SelectValue placeholder="Select vehicle" />
                </SelectTrigger>
                <SelectContent>
                  {vehicleMaster.map(v => (
                    <SelectItem key={v} value={v}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                data-testid="vehicle-number-input"
                value={dispatchForm.vehicle_number}
                onChange={e => setDispatchForm(f => ({ ...f, vehicle_number: e.target.value }))}
                placeholder="e.g. KA-01-AB-1234"
                className="h-10"
              />
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Remarks</Label>
            <Textarea
              data-testid="dispatch-remarks-input"
              value={dispatchForm.remarks}
              onChange={e => setDispatchForm(f => ({ ...f, remarks: e.target.value }))}
              placeholder="Optional dispatch remarks..."
              rows={2}
            />
          </div>
          <Button
            data-testid="create-dispatch-entry"
            onClick={onSubmit}
            disabled={actionLoading || (isPartial && (!dispatchForm.windows_dispatched || dispatchForm.windows_dispatched > remaining))}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold h-11"
          >
            {actionLoading ? 'Creating...' : `Create ${isPartial ? 'Partial' : 'Full'} Dispatch Entry`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
